import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import BarcodeScanner from './BarcodeScanner';
import PackagingGiftSetView from './PackagingGiftSetView';
import useStationIdentity from '../../hooks/useStationIdentity';
import PourSessionPanel from './PourSessionPanel';
import SealedCartonsPanel from './SealedCartonsPanel';
import './DepartmentScreen.css';

/**
 * Base component for department operation screens
 * Provides common functionality for all production departments
 */
function DepartmentScreen({
  department,
  departmentName,
  nextDepartment,
  color = '#007bff'
}) {
  // Which physical station this screen is. Comes from ?station=WICK-2 on the kiosk
  // URL; without it Wicking 1 and Wicking 2 are indistinguishable in the data.
  const station = useStationIdentity(department);
  const [pourRefresh, setPourRefresh] = useState(0);

  const [carts, setCarts] = useState([]);
  const [selectedCart, setSelectedCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [wickInventory, setWickInventory] = useState([]);
  const [vesselTotals, setVesselTotals] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [currentOperator, setCurrentOperator] = useState(null);
  const [cartVesselSummary, setCartVesselSummary] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showGoodbye, setShowGoodbye] = useState(false);
  const [goodbyeName, setGoodbyeName] = useState('');
  const [operatorStats, setOperatorStats] = useState(null);
  const [activeCart, setActiveCart] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [serverVersion, setServerVersion] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState(null);
  const scanInputRef = useRef(null);
  const INACTIVITY_TIMEOUT = 90 * 60 * 1000; // 90 minutes (wicking can take up to an hour)

  // Fetch carts in current department
  const fetchCarts = useCallback(async () => {
    try {
      const response = await axios.get(`/api/departments/${department}/carts`);
      setCarts(response.data);
    } catch (error) {
      console.error('Error fetching carts:', error);
    }
  }, [department]);

  // Fetch department metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await axios.get(`/api/departments/${department}/metrics?dateRange=today`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  }, [department]);

  // Fetch overall workload
  const fetchWorkload = useCallback(async () => {
    try {
      const response = await axios.get('/api/production/workload');
      setWorkload(response.data);
    } catch (error) {
      console.error('Error fetching workload:', error);
    }
  }, []);

  // Fetch wick inventory (wicking department only)
  const fetchWickInventory = useCallback(async () => {
    if (department !== 'wicking') return;
    try {
      const response = await axios.get('/api/departments/wicking/wick-inventory');
      setWickInventory(response.data);
    } catch (error) {
      console.error('Error fetching wick inventory:', error);
    }
  }, [department]);

  // Fetch vessel totals (carting department only)
  const fetchVesselTotals = useCallback(async () => {
    if (department !== 'carting') return;
    try {
      const response = await axios.get('/api/departments/carting/vessel-totals');
      setVesselTotals(response.data);
    } catch (error) {
      console.error('Error fetching vessel totals:', error);
    }
  }, [department]);

  // Fetch operator stats
  const fetchOperatorStats = useCallback(async (operatorId) => {
    if (!operatorId) return;
    try {
      // Fetch today's stats
      const todayResponse = await axios.get(`/api/operators/${operatorId}/performance?department=${department}&dateRange=today`);
      // Fetch month stats
      const monthResponse = await axios.get(`/api/operators/${operatorId}/performance?department=${department}&dateRange=month`);

      setOperatorStats({
        today: todayResponse.data,
        month: monthResponse.data
      });
    } catch (error) {
      console.error('Error fetching operator stats:', error);
    }
  }, [department]);

  // Load all data
  const loadData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    await Promise.all([fetchCarts(), fetchMetrics(), fetchWorkload(), fetchWickInventory(), fetchVesselTotals()]);
    if (showLoader) setLoading(false);
  }, [fetchCarts, fetchMetrics, fetchWorkload, fetchWickInventory, fetchVesselTotals]);

  // Load saved operator on mount
  useEffect(() => {
    const savedOperator = localStorage.getItem('currentOperator');
    if (savedOperator) {
      try {
        const parsed = JSON.parse(savedOperator);
        setCurrentOperator(parsed);
        setLastActivityTime(Date.now());
      } catch (e) {
        localStorage.removeItem('currentOperator');
      }
    }
  }, []);

  // Inactivity timeout - auto logout after 90 minutes (skip when paused)
  useEffect(() => {
    if (!currentOperator || isPaused) return;

    const checkInactivity = setInterval(() => {
      if (Date.now() - lastActivityTime > INACTIVITY_TIMEOUT) {
        // Auto logout
        setGoodbyeName(currentOperator.first_name);
        setShowGoodbye(true);
        setTimeout(() => {
          setShowGoodbye(false);
          setGoodbyeName('');
        }, 2000);

        setCurrentOperator(null);
        setOperatorStats(null);
        setActiveCart(null);
        setIsPaused(false);
        setPausedAt(null);
        localStorage.removeItem('currentOperator');
        localStorage.removeItem('username');
        // Record the walk-away as a timeout rather than a logout.
        station.closeSession('timeout');
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkInactivity);
  }, [currentOperator, lastActivityTime, isPaused]);

  // Reset activity timer on user interaction
  const resetActivityTimer = useCallback(() => {
    setLastActivityTime(Date.now());
  }, []);

  // Handle PIN digit press
  const handlePinDigit = (digit) => {
    if (pinInput.length < 4) {
      const newPin = pinInput + digit;
      setPinInput(newPin);
      setPinError('');

      // Auto-submit when 4 digits entered
      if (newPin.length === 4) {
        submitPin(newPin);
      }
    }
  };

  // Clear PIN input
  const handlePinClear = () => {
    setPinInput('');
    setPinError('');
  };

  // Submit PIN for authentication
  const submitPin = async (pin) => {
    try {
      const response = await axios.post('/api/operators/pin', { pin });
      const op = response.data;

      // Log in the operator
      setCurrentOperator(op);
      localStorage.setItem('currentOperator', JSON.stringify(op));
      localStorage.setItem('username', op.display_name);
      setLastActivityTime(Date.now());

      // Open a server-side session so "who is at this station" is answerable and
      // presence can be measured. Never blocks the login if it fails.
      station.openSession(op, 'pin');

      // Show welcome screen
      setShowWelcome(true);
      setTimeout(() => {
        setShowWelcome(false);
        if (scanInputRef.current) {
          scanInputRef.current.focus();
        }
      }, 2500);

      // Fetch operator stats
      fetchOperatorStats(op.id);

      // Clear PIN
      setPinInput('');
    } catch (error) {
      setPinError('Invalid PIN');
      setPinInput('');
    }
  };

  // Handle logout
  const handleLogout = () => {
    if (currentOperator) {
      setGoodbyeName(currentOperator.first_name);
      setShowGoodbye(true);
      setTimeout(() => {
        setShowGoodbye(false);
        setGoodbyeName('');
      }, 2000);
    }

    setCurrentOperator(null);
    setOperatorStats(null);
    setActiveCart(null);
    setIsPaused(false);
    setPausedAt(null);
    localStorage.removeItem('currentOperator');
    localStorage.removeItem('username');
    setPinInput('');
    station.closeSession('logout');
  };

  // Handle pause - lock screen but keep session
  const handlePause = () => {
    setIsPaused(true);
    setPausedAt(Date.now());
  };

  // Resume session from pause
  const handleResume = () => {
    // Calculate pause duration and adjust cart timer
    if (pausedAt && activeCart) {
      const pauseDuration = Date.now() - pausedAt;
      setActiveCart(prev => ({
        ...prev,
        scannedInAt: new Date(new Date(prev.scannedInAt).getTime() + pauseDuration)
      }));
    }

    // Resume session
    setIsPaused(false);
    setPausedAt(null);
    setLastActivityTime(Date.now());

    // Refocus scan input
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  };

  useEffect(() => {
    loadData();

    // Focus the scan input on mount
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }

    // Set up auto-refresh every 30 seconds (silent refresh)
    const interval = setInterval(() => loadData(false), 30000);
    setRefreshInterval(interval);

    // Update timer every second for elapsed time display
    const timerInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      if (interval) clearInterval(interval);
      clearInterval(timerInterval);
    };
  }, [loadData]);

  // Check for server-triggered reload (for remote kiosk refresh)
  useEffect(() => {
    let initialVersion = null;

    const checkVersion = async () => {
      try {
        const response = await axios.get('/api/version');
        const { version, reload } = response.data;

        // Store initial version on first check
        if (initialVersion === null) {
          initialVersion = version;
          setServerVersion(version);
          setLastUpdated(new Date());
          return;
        }

        // Reload if force flag is set or version changed
        if (reload || version !== initialVersion) {
          console.log('[RELOAD] Server triggered reload');
          window.location.reload();
        }
      } catch (error) {
        // Silently ignore - server might be restarting
      }
    };

    // Check every 10 seconds
    const versionInterval = setInterval(checkVersion, 10000);
    checkVersion(); // Initial check

    return () => clearInterval(versionInterval);
  }, []);

  // Scanner buffer - collect rapid keystrokes
  const scanBuffer = useRef('');
  const scanTimeout = useRef(null);
  const handleScanRef = useRef(null);

  // Handle barcode scan - cart barcodes only (PIN handles operator login)
  const handleScan = useCallback(async (barcode) => {
    const useTimeTracking = ['carting', 'wicking', 'pouring'].includes(department);

    // Reset activity timer on scan
    resetActivityTimer();

    // Check localStorage for operator (state may be stale in closure)
    const savedOp = localStorage.getItem('currentOperator');
    const operator = savedOp ? JSON.parse(savedOp) : null;

    // Require operator login for time-tracked departments
    if (useTimeTracking && !operator) {
      setSuccessMessage('Enter your PIN first');
      setTimeout(() => setSuccessMessage(''), 3000);
      return;
    }

    // A blend sheet, not a cart. Pouring is continuous from a melter across many
    // carts, so the blend sheet is the work unit there -- scanning it starts the
    // pour clock. Routed before the cart lookup because BLEND-#### is not a cart
    // barcode and would just fail.
    if (/^BLEND[-\s]?\d+$/i.test(String(barcode).trim())) {
      if (department !== 'pouring') {
        setSuccessMessage('✗ Blend sheets are scanned at the pouring station');
        setTimeout(() => setSuccessMessage(''), 3000);
        return;
      }
      try {
        const { data } = await axios.post('/api/station-floor/pour/start', {
          blend_number: String(barcode).trim(),
          station_code: station.stationCode,
          operator_name: operator?.display_name || localStorage.getItem('username') || null,
          operator_id: operator?.id ?? null,
          session_id: station.sessionId
        });
        setPourRefresh((n) => n + 1);
        setSuccessMessage(`✓ Pouring ${data.blend_number} started`);
      } catch (err) {
        setSuccessMessage(`✗ ${err.response?.data?.error || 'Could not start pour'}`);
      }
      setTimeout(() => setSuccessMessage(''), 3000);
      setManualInput('');
      if (scanInputRef.current) scanInputRef.current.focus();
      return;
    }

    try {
      // First get cart by barcode
      const cartResponse = await axios.get(`/api/carts/barcode/${barcode}`);
      const cart = cartResponse.data;

      if (useTimeTracking) {
        // Check if cart is already scanned in
        const statusResponse = await axios.get(`/api/carts/${cart.id}/scan-status`);
        const scanStatus = statusResponse.data;

        const username = operator?.display_name || localStorage.getItem('username') || 'Operator';
        // Legacy field, kept so old reports still read; station_code is the real key.
        const workstation = `${department.toUpperCase()}-1`;
        const ctx = station.scanContext(operator);

        if (scanStatus.isScannedIn && scanStatus.currentDepartment === department) {
          // Second scan - scan OUT and move to next department
          const scanOutResult = await axios.post(`/api/carts/${cart.id}/scan-out`, {
            department,
            scannedBy: username,
            workstation,
            ...ctx
          });

          setCartVesselSummary(null);
          setActiveCart(null); // Clear active cart on scan out
          // Refresh operator stats after completing a cart
          if (operator?.id) {
            fetchOperatorStats(operator.id);
          }
          setSuccessMessage(`✓ Completed in ${scanOutResult.data.durationMinutes} min`);
        } else {
          // First scan - scan IN to start work
          await axios.post(`/api/carts/${cart.id}/scan-in`, {
            department,
            scannedBy: username,
            workstation,
            ...ctx
          });

          // Build vessel summary for active cart display
          const vesselSummary = {};
          cart.cart_items?.forEach(item => {
            const vesselName = (item.vessel_name || 'Unknown').replace(/^VES-/i, '');
            vesselSummary[vesselName] = (vesselSummary[vesselName] || 0) + (item.quantity || 0);
          });
          const oilName = (cart.batch?.fragrance_oil_name || 'Unknown').replace(/^OIL-/i, '');
          const totalUnits = cart.cart_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

          // Set active cart for display
          setActiveCart({
            id: cart.id,
            cartNumber: cart.cart_number?.replace(/^CART-\d{8}-/, '') || cart.barcode,
            barcode: cart.barcode,
            oilName,
            vessels: vesselSummary,
            totalUnits,
            scannedInAt: new Date()
          });

          setSuccessMessage(`✓ Cart ${cart.barcode} started`);
        }
      } else {
        // Packaging or other departments - single scan workflow (legacy behavior).
        // Packaging has no elapsed capture at all yet: one scan advances the cart, so
        // there is no scan_in to measure against. Individual packing stations need the
        // two-scan flow before packaging productivity can be measured.
        const scanData = {
          scannedBy: localStorage.getItem('username') || 'Operator',
          department: department,
          workstation: `${department.toUpperCase()}-1`,
          scanType: 'barcode',
          ...station.scanContext(operator)
        };

        await axios.post(`/api/carts/${cart.id}/scan`, scanData);
        setSuccessMessage(`✓ Cart ${cart.barcode} moved to ${nextDepartment || 'completed'}`);
      }

      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh data
      await loadData();
      setScanning(false);

      // Clear input and refocus
      setManualInput('');
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    } catch (error) {
      console.error('Error processing scan:', error);
      setSuccessMessage(`✗ ${error.response?.data?.error || 'Failed to process cart'}`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Clear input and refocus on error
      setManualInput('');
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    }
    // scanContext is memoized on the station code, so handleScan stays stable across
    // renders -- depending on the whole `station` object would rebuild it every render.
  }, [department, resetActivityTimer, fetchOperatorStats, loadData, nextDepartment,
      station.scanContext, station.stationCode, station.sessionId]);

  // Keep ref updated for use in event handlers
  handleScanRef.current = handleScan;

  // Capture ALL keystrokes globally - scanner types fast, humans type slow
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Prevent F5, Ctrl+R refresh
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        e.preventDefault();
        return;
      }

      // Skip if in modal or on PIN keypad
      if (e.target.closest('.modal-content') || e.target.closest('.pin-keypad')) return;

      // For any keypress, ensure focus is on our input (only if logged in)
      if (scanInputRef.current && document.activeElement !== scanInputRef.current) {
        scanInputRef.current.focus();
      }

      // Build scan buffer for rapid input (scanner sends keys fast)
      if (e.key.length === 1) { // printable character
        scanBuffer.current += e.key;

        // Clear previous timeout
        if (scanTimeout.current) clearTimeout(scanTimeout.current);

        // Process after 100ms of no input (scanner is done)
        scanTimeout.current = setTimeout(() => {
          const scanned = scanBuffer.current.trim();
          if (scanned && scanned.length > 3) {
            // Extract barcode and process
            const barcode = scanned.match(/(CART-[\w-]+)/i)?.[1] || scanned.split(/\s+/).pop();
            if (barcode && handleScanRef.current) {
              setManualInput('');
              handleScanRef.current(barcode);
            }
          }
          scanBuffer.current = '';
        }, 100);
      }

      // On Enter, also try to process immediately
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();

        if (scanBuffer.current.trim()) {
          const scanned = scanBuffer.current.trim();
          const barcode = scanned.match(/(CART-[\w-]+)/i)?.[1] || scanned.split(/\s+/).pop();
          if (barcode && handleScanRef.current) {
            handleScanRef.current(barcode);
          }
          scanBuffer.current = '';
          if (scanTimeout.current) clearTimeout(scanTimeout.current);
        }
      }
    };

    const handleGlobalClick = (e) => {
      // Don't steal focus from PIN keypad
      if (e.target.closest('.pin-keypad')) return;
      if (e.target.tagName !== 'BUTTON' && !e.target.closest('.modal-content') && scanInputRef.current) {
        setTimeout(() => scanInputRef.current.focus(), 100);
      }
    };

    // Capture at window level with capture phase
    window.addEventListener('keydown', handleGlobalKeyDown, true);
    document.addEventListener('click', handleGlobalClick);

    // Keep focus on scan input (only when logged in)
    const focusInterval = setInterval(() => {
      if (scanInputRef.current && document.activeElement !== scanInputRef.current &&
          !document.activeElement.closest('.modal-content') &&
          !document.activeElement.closest('.pin-keypad')) {
        scanInputRef.current.focus();
      }
    }, 200);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, true);
      document.removeEventListener('click', handleGlobalClick);
      clearInterval(focusInterval);
      if (scanTimeout.current) clearTimeout(scanTimeout.current);
    };
  }, []);

  // Extract barcode from scanner input (may include timestamp prefix)
  const extractBarcode = (input) => {
    // Scanner may send: "10:24 AM 1/27/2026    sa:OP0024"
    // We want just the barcode part (sa:OP0024 or CART-xxx, etc.)
    const trimmed = input.trim();

    // Look for known barcode patterns at the end
    const barcodeMatch = trimmed.match(/(sa:OP\d+|CART-[\w-]+|[A-Z]{2,4}-\d+)$/i);
    if (barcodeMatch) {
      return barcodeMatch[1];
    }

    // If no pattern match, take the last "word" (after any whitespace)
    const parts = trimmed.split(/\s+/);
    return parts[parts.length - 1];
  };

  // Handle keydown on input - process on Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const rawInput = manualInput.trim();
      const barcode = extractBarcode(rawInput);
      if (barcode) {
        setManualInput('');
        handleScan(barcode).catch(error => {
          console.error('Error processing scan:', error);
          setSuccessMessage(`✗ Error: ${error.message}`);
          setTimeout(() => setSuccessMessage(''), 3000);
        });
      }
    }
  };

  // Complete cart processing - same logic as scan out
  const completeCart = async (cartId) => {
    const useTimeTracking = ['carting', 'wicking', 'pouring'].includes(department);
    if (useTimeTracking && !currentOperator) {
      setSuccessMessage('Please scan your badge first');
      setTimeout(() => setSuccessMessage(''), 3000);
      return;
    }

    try {
      const username = currentOperator?.display_name || localStorage.getItem('username') || 'Operator';
      const workstation = `${department.toUpperCase()}-1`;

      if (useTimeTracking) {
        // Check if cart is scanned in
        const statusResponse = await axios.get(`/api/carts/${cartId}/scan-status`);
        const scanStatus = statusResponse.data;

        if (scanStatus.isScannedIn && scanStatus.currentDepartment === department) {
          // Scan out
          const scanOutResult = await axios.post(`/api/carts/${cartId}/scan-out`, {
            department,
            scannedBy: username,
            workstation
          });

          setSuccessMessage(`✓ Cart completed in ${scanOutResult.data.durationMinutes} min → ${nextDepartment || 'completed'}`);
        } else {
          // Need to scan in first
          setSuccessMessage(`✗ Cart must be scanned in before completing`);
          setTimeout(() => setSuccessMessage(''), 3000);
          return;
        }
      } else {
        // Packaging - use old workflow
        const scanData = {
          scannedBy: username,
          department: department,
          workstation: workstation,
          scanType: 'manual'
        };

        await axios.post(`/api/carts/${cartId}/scan`, scanData);
        setSuccessMessage(`✓ Cart completed → ${nextDepartment || 'completed'}`);
      }

      setTimeout(() => setSuccessMessage(''), 3000);
      await loadData();
    } catch (error) {
      console.error('Error completing cart:', error);
      alert(error.response?.data?.error || 'Failed to complete cart');
    }
  };

  // Calculate elapsed time for scanned-in cart
  const getElapsedTime = (scannedInAt) => {
    if (!scannedInAt) return null;
    const start = new Date(scannedInAt);
    const elapsed = (currentTime - start) / 1000; // seconds
    const minutes = Math.floor(elapsed / 60);
    const seconds = Math.floor(elapsed % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate time per unit
  const getTimePerUnit = (elapsedMinutes, unitCount) => {
    if (!elapsedMinutes || !unitCount || unitCount === 0) return null;
    const timePerUnit = elapsedMinutes / unitCount;
    return timePerUnit.toFixed(2);
  };

  // Calculate total units on cart
  const getTotalUnits = (cart) => {
    return cart.cart_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Get elapsed time for active cart
  const getActiveCartElapsed = () => {
    if (!activeCart?.scannedInAt) return '0:00';
    const elapsed = (currentTime - new Date(activeCart.scannedInAt)) / 1000;
    const minutes = Math.floor(elapsed / 60);
    const seconds = Math.floor(elapsed % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="department-screen loading">
        <div className="loader">Loading {departmentName}...</div>
      </div>
    );
  }

  // Welcome screen - fullscreen greeting
  if (showWelcome && currentOperator) {
    return (
      <div className="welcome-screen">
        <div className="welcome-content">
          <h1 className="welcome-greeting">{getGreeting()},</h1>
          <h2 className="welcome-name">{currentOperator.first_name}</h2>
        </div>
      </div>
    );
  }

  // Goodbye screen - fullscreen farewell
  if (showGoodbye) {
    return (
      <div className="welcome-screen goodbye-screen">
        <div className="welcome-content">
          <h1 className="welcome-greeting">Catch you later,</h1>
          <h2 className="welcome-name">{goodbyeName}</h2>
        </div>
      </div>
    );
  }

  // Paused screen - tap to resume
  if (isPaused && currentOperator) {
    return (
      <div className="department-screen operator-login-screen paused-screen">
        <div className="login-prompt-container">
          <h1 className="login-title">Paused</h1>
          <p className="paused-operator">{currentOperator.first_name}</p>
          {activeCart && (
            <div className="paused-cart-info">
              <span>Cart #{activeCart.cartNumber}</span>
              <span className="paused-cart-oil">{activeCart.oilName}</span>
            </div>
          )}
          <button className="resume-btn" onClick={handleResume}>
            <span className="play-icon">&#9658;</span>
            <span>Resume</span>
          </button>
          <button className="paused-signout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // No operator logged in - show PIN entry
  if (!currentOperator) {
    return (
      <div className="department-screen operator-login-screen">
        <div className="login-prompt-container">
          <h1 className="login-title">{departmentName}</h1>
          <div className="pin-entry-box">
            <p className="login-instruction">Enter your PIN</p>
            <div className="pin-display">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`pin-dot ${i < pinInput.length ? 'filled' : ''}`} />
              ))}
            </div>
            {pinError && <p className="pin-error">{pinError}</p>}
            <div className="pin-keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                <button
                  key={digit}
                  className="pin-key"
                  onClick={() => handlePinDigit(digit.toString())}
                >
                  {digit}
                </button>
              ))}
              <button className="pin-key pin-clear" onClick={handlePinClear}>
                Clear
              </button>
              <button
                className="pin-key"
                onClick={() => handlePinDigit('0')}
              >
                0
              </button>
              <div className="pin-key pin-spacer" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main operator dashboard
  return (
    <div className="department-screen operator-dashboard">
      {/* Header with operator name and scan input */}
      <div className="dashboard-header">
        <div className="operator-header">
          <span className="operator-greeting">{currentOperator.first_name}</span>
          <span className="department-label">{departmentName}</span>
          <div className="header-buttons">
            <button className="pause-btn" onClick={handlePause}>Pause</button>
            <button className="logout-btn" onClick={handleLogout}>Sign Out</button>
          </div>
        </div>
        <div className="scan-input-container">
          <input
            ref={scanInputRef}
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={department === 'pouring' ? 'Scan cart or blend sheet...' : 'Scan cart...'}
            className="dashboard-scan-input"
            autoFocus
          />
          {successMessage && (
            <div className={`scan-feedback ${successMessage.startsWith('✓') ? 'success' : 'error'}`}>
              {successMessage}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="operator-stats">
        <div className="stat-card">
          <span className="stat-value">{operatorStats?.today?.totalUnits || 0}</span>
          <span className="stat-label">Units Today</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{operatorStats?.today?.avgTimePerUnit ? Math.round(operatorStats.today.avgTimePerUnit * 60) : '-'}</span>
          <span className="stat-label">Sec/Unit Today</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{operatorStats?.month?.totalUnits || 0}</span>
          <span className="stat-label">Units This Month</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{operatorStats?.month?.avgTimePerUnit ? Math.round(operatorStats.month.avgTimePerUnit * 60) : '-'}</span>
          <span className="stat-label">Avg Sec/Unit</span>
        </div>
      </div>

      {/* Pour session -- pouring only. The blend sheet is the work unit here, and
          pause lets the operator declare non-pour time instead of being charged
          for it. */}
      {department === 'pouring' && (
        <PourSessionPanel
          key={pourRefresh}
          stationCode={station.stationCode}
          operator={currentOperator}
          sessionId={station.sessionId}
          onMessage={(msg) => {
            setSuccessMessage(msg);
            setTimeout(() => setSuccessMessage(''), 3000);
          }}
        />
      )}

      {/* Sealed cartons -- packaging only. The sealer camera counts every
          carton by SKU; the lead confirms the suggested assembly build here. */}
      {department === 'packaging' && (
        <SealedCartonsPanel
          station={station.stationCode || 'PACKAGING'}
          operator={currentOperator}
        />
      )}

      {/* Active Cart Card */}
      <div className="active-cart-section">
        {activeCart ? (
          <div className="active-cart-card">
            <div className="active-cart-header">
              <span className="active-cart-number">Cart #{activeCart.cartNumber}</span>
              <span className="active-cart-timer">{getActiveCartElapsed()}</span>
            </div>
            <div className="active-cart-oil">{activeCart.oilName}</div>
            <div className="active-cart-vessels">
              {Object.entries(activeCart.vessels).map(([vessel, qty]) => (
                <div key={vessel} className="vessel-chip">
                  <span className="vessel-qty">{qty}</span>
                  <span className="vessel-name">{vessel}</span>
                </div>
              ))}
            </div>
            <div className="active-cart-total">{activeCart.totalUnits} units total</div>
            <div className="active-cart-hint">Scan cart again when complete</div>
          </div>
        ) : (
          <div className="no-active-cart">
            <p>Scan a cart to begin</p>
          </div>
        )}
      </div>

      {/* Queue indicator */}
      {workload && (
        <div className="queue-indicator">
          <span className="queue-count">{workload[department]?.carts || 0}</span>
          <span className="queue-label">carts in queue</span>
        </div>
      )}

      {/* Version indicator */}
      {lastUpdated && (
        <div className="version-indicator">
          {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

export default DepartmentScreen;